/**
 * Dynamic Data/View helper methods for LoaderNormal
 * Public applyDynamicViewHandlers with skip guards wraps private _applyDynamicViewHandlers
 * Applies dynamic data handlers from Global/dynamicdata.json if configured
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonUtility } from '../../app/jsonUtility.js';
import { JsonArray, JsonObject, JsonValue, JsonValueType } from '../../app/json/jsonValue.js';
import { NavigationDecision } from '../../model/index.js';
import { RegistryStore } from '../../registry/registryStore.js';
import { CommonUtil } from '../../common/commonUtil.js';
import { DynamicConfig } from '../../registry/dynamicConfig.js';
import { AppSiteResult } from '../../registry/iface/appSiteResult.js';
import type { GetParentJsonValueHandler } from '../../registry/iface/getParentJsonValueHandler.js';
import type { DynamicConfigMap } from '../../registry/dynamicConfigMap.js';
import type { LoaderNormal } from './loaderNormal.js';

type PlainJsonObject = { [key: string]: unknown };


/**
 * Finds a key in a Map case-insensitively
 */
function findKeyInMapCaseInsensitive(map: DynamicConfigMap, key: string): string | null {
    if (!map || !key) return null;
    const lowerKey = key.toLowerCase();
    for (const k of map.keys()) {
        if (k.toLowerCase() === lowerKey) {
            return k;
        }
    }
    return null;
}

function executeWithReadOnly<T>(
    paramJson: JsonObject,
    componentJson: JsonObject,
    configJson: JsonObject | null,
    action: () => T
): T {
    const paramWasReadOnly = paramJson.isReadOnly;
    const componentWasReadOnly = componentJson.isReadOnly;
    const configWasReadOnly = configJson ? configJson.isReadOnly : false;

    paramJson.isReadOnly = true;
    componentJson.isReadOnly = true;
    if (configJson) {
        configJson.isReadOnly = true;
    }

    try {
        return action();
    } finally {
        paramJson.isReadOnly = paramWasReadOnly;
        componentJson.isReadOnly = componentWasReadOnly;
        if (configJson) {
            configJson.isReadOnly = configWasReadOnly;
        }
    }
}

/**
 * Dynamic data methods to be attached to LoaderNormal prototype
 */
export const dynamicMethods = {
    _hasDynamicDataHandlers(this: LoaderNormal, appSite: string, componentName: string): boolean {
        const config = this.getDynamicDataConfig(appSite);
        if (!config) return false;
        if (config.has('*')) return true;
        const name = componentName.toLowerCase();
        for (const key of config.keys()) if (key.toLowerCase() === name) return true;
        return false;
    },

    _hasDynamicActionHandlers(this: LoaderNormal, appSite: string, componentName: string): boolean {
        const config = this.getDynamicActionConfig(appSite);
        if (!config) return false;
        if (config.has('*')) return true;
        const name = componentName.toLowerCase();
        for (const key of config.keys()) if (key.toLowerCase() === name) return true;
        return false;
    },

    // True when a CLIENT-DATA preparer (dynamicclientdata.json) is bound to this component — mirrors the
    // resolution applyDynamicClientData uses (search-appsite aware). The inheritance fast-path must honor this
    // so the Embedded island is produced even when the component carries no server Data/Action/Global handler
    // (e.g. the client re-merge, where server-only config is not shipped).
    _hasDynamicClientDataHandlers(this: LoaderNormal, appSite: string, componentName: string): boolean {
        const { dynamicConfig } = this.resolveDynamicClientDataConfigSource(appSite);
        if (!dynamicConfig) return false;
        return this.getDynamicDataMatchKey(componentName, dynamicConfig) !== null;
    },

    getTemplateWithView(this: LoaderNormal, appSite: string, currentTemplateName: string, componentName: string, appView: string | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false) {
        // clientMode threads into getTemplate → the inheritance seam, where applyClientHandlers consumes the
        // delivered island and enriches resolvedJson (the client mirror of the server data handler).
        const result = this.getTemplate(appSite, currentTemplateName, componentName, appView, flowStack, configJson, clientMode);
        const html = this.applyDynamicViewHandlers(appSite, appView || '', currentTemplateName, componentName, flowStack?.length ?? 0, result.html, result.json, result.resolvedAppSite, result.resolvedTemplateName, configJson);
        return { ...result, html };
    },

    // #region DynamicData Helpers (Private)

    /**
     * Builds an immutable parent context map from the flow stack.
     * Walks forward through flowStack, collecting all parent key-value pairs.
     * Later parents overwrite earlier ones; resolved values overwrite static cache values.
     */
    _buildParentContext(this: LoaderNormal, appSite: string, flowStack: string[]): Map<string, string> | null {
        if (!flowStack || flowStack.length === 0) {
            return null;
        }

        const context = new Map<string, string>();
        const appSiteLower = appSite.toLowerCase();

        for (let i = 0; i < flowStack.length; i++) {
            const parentName = flowStack[i];
            const parentKey = `${appSiteLower}_${parentName.toLowerCase()}`;

            const cached = this._appSiteCache.get(appSite);
            if (cached) {
                const parentTemplate = cached.templates.get(parentKey);
                if (parentTemplate && parentTemplate.json) {
                    for (const [jsonKey, jsonValue] of parentTemplate.json.entries()) {
                        let strValue: string | null = null;
                        if (typeof jsonValue === 'string') {
                            strValue = jsonValue;
                        } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                            strValue = jsonValue.asString();
                        }
                        if (strValue !== null) {
                            context.set(jsonKey.toLowerCase(), strValue);
                        }
                    }
                }
            }

            const resolvedJson = this._dynamicResults.resolvedTemplateJson.get(parentKey).asObject();
            if (resolvedJson) {
                for (const [jsonKey, jsonValue] of resolvedJson.entries()) {
                    let strValue: string | null = null;
                    if (typeof jsonValue === 'string') {
                        strValue = jsonValue;
                    } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                        strValue = jsonValue.asString();
                    }
                    if (strValue !== null) {
                        context.set(jsonKey.toLowerCase(), strValue);
                    }
                }
            }
        }

        return context.size > 0 ? context : null;
    },
    // NOTE: Private to this _Dynamic file - no _ILoader call site, matching the private members of C# LoaderNormal_Dynamic.

    executeWithReadOnly<T>(this: LoaderNormal, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, action: () => T): T {
        return executeWithReadOnly(paramJson, componentJson, configJson, action);
    },

    // #endregion

    // #region DynamicView Helpers (Private)
    // NOTE: Private to this _Dynamic file - no _ILoader call site, matching the private members of C# LoaderNormal_Dynamic.

    shouldSkipDynamicViewHandlerInvocation(this: LoaderNormal, componentHtml: string, componentName: string, resolvedComponentName: string): boolean {
        return !componentHtml ||
            componentName.startsWith('!') ||
            resolvedComponentName === this._config!.defaultSkipTemplateName ||
            resolvedComponentName === this._config!.defaultReplaceTemplateName;
    },

    resolveDynamicViewConfigSource(this: LoaderNormal, appSite: string): { dynamicConfig: DynamicConfigMap | null; dynamicConfigAppSite: string } {
        let dynamicConfig = this.getDynamicViewConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (dynamicConfig) {
            return { dynamicConfig, dynamicConfigAppSite };
        }

        for (const searchAppSite of this.buildAppSiteSearchOrder(appSite).slice(1)) {
            dynamicConfig = this.getDynamicViewConfig(searchAppSite);
            if (dynamicConfig) {
                dynamicConfigAppSite = searchAppSite;
                break;
            }
        }

        return { dynamicConfig, dynamicConfigAppSite };
    },

    getDynamicViewKeysToProcess(this: LoaderNormal, appSite: string, componentName: string, dynamicConfig: DynamicConfigMap, dynamicConfigAppSite: string): { keysToProcess: string[]; dynamicConfig: DynamicConfigMap; dynamicConfigAppSite: string } {
        const keysToProcess: string[] = [];
        if (dynamicConfig.has('*')) {
            keysToProcess.push('*');
        }

        let foundSpecificMatch = false;
        const matchedKey = findKeyInMapCaseInsensitive(dynamicConfig, componentName);
        if (matchedKey && matchedKey !== '*') {
            keysToProcess.push(matchedKey);
            foundSpecificMatch = true;
        }

        if (!foundSpecificMatch) {
            for (const searchAppSite of this.buildAppSiteSearchOrder(appSite).slice(1)) {
                const searchConfig = this.getDynamicViewConfig(searchAppSite);
                if (!searchConfig) {
                    continue;
                }

                const searchMatchedKey = findKeyInMapCaseInsensitive(searchConfig, componentName);
                if (searchMatchedKey && searchMatchedKey !== '*') {
                    keysToProcess.push(searchMatchedKey);
                    dynamicConfig = searchConfig;
                    dynamicConfigAppSite = searchAppSite;
                    foundSpecificMatch = true;
                    break;
                }

                if (!keysToProcess.includes('*') && searchConfig.has('*')) {
                    keysToProcess.unshift('*');
                    dynamicConfig = searchConfig;
                    dynamicConfigAppSite = searchAppSite;
                    break;
                }
            }
        }

        return { keysToProcess, dynamicConfig, dynamicConfigAppSite };
    },

    shouldSkipViewHandler(this: LoaderNormal, iViewType: string): boolean {
        if (!RegistryStore.containsView(iViewType)) {
            return true;
        }

        if (!this._skipDynamicView || this._skipDynamicView.trim() === '') {
            return false;
        }

        const normalizedPatterns = this._skipDynamicView.trim();
        if (normalizedPatterns === '*') {
            return true;
        }

        const lowerViewType = iViewType.toLowerCase();
        for (const pattern of normalizedPatterns.split(',')) {
            const trimmedPattern = pattern.trim().toLowerCase();
            if (!trimmedPattern) {
                continue;
            }

            if (trimmedPattern === '*' || trimmedPattern === lowerViewType) {
                return true;
            }
        }

        return false;
    },

    tryResolveDynamicViewProvider(this: LoaderNormal, iViewType: string, dynamicConfigAppSite: string, paramFile: string): { viewProvider: ReturnType<typeof RegistryStore.getDynamicView> | null; paramJson: JsonObject } {
        const paramJson = this.getDynamicViewParams(dynamicConfigAppSite, paramFile) ?? new JsonObject();
        if (!RegistryStore.containsView(iViewType)) {
            return { viewProvider: null, paramJson };
        }

        return {
            viewProvider: RegistryStore.getDynamicView(iViewType),
            paramJson
        };
    },

    executeDynamicViewHandler(this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentHtml: string, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, viewProvider: NonNullable<ReturnType<typeof RegistryStore.getDynamicView>>): string {
        const handlerStart = performance.now();
        const resultHtml = this.executeWithReadOnly(
            paramJson,
            componentJson,
            configJson,
            () => RegistryStore.invokeDynamicView(viewProvider.typeName, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, componentHtml, paramJson, componentJson, configJson)
        );
        this._dynamicViewNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
        return resultHtml ?? componentHtml;
    },

    recordDynamicViewResult(this: LoaderNormal, componentName: string, iViewType: string, resultHtml: string): void {
        this._dynamicResults.dynamicViewResults.set(`${componentName}_${iViewType}`, JsonValue.string(resultHtml));
    },

    resolveDynamicDataConfigSource(this: LoaderNormal, appSite: string): { dynamicConfig: DynamicConfigMap | null; dynamicConfigAppSite: string } {
        let dynamicConfig = this.getDynamicDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (dynamicConfig) {
            return { dynamicConfig, dynamicConfigAppSite };
        }

        for (const searchAppSite of this.buildAppSiteSearchOrder(appSite).slice(1)) {
            dynamicConfig = this.getDynamicDataConfig(searchAppSite);
            if (dynamicConfig) {
                dynamicConfigAppSite = searchAppSite;
                break;
            }
        }

        return { dynamicConfig, dynamicConfigAppSite };
    },

    getDynamicDataMatchKey(this: LoaderNormal, componentName: string, dynamicConfig: DynamicConfigMap): string | null {
        const matchedKey = findKeyInMapCaseInsensitive(dynamicConfig, componentName);
        if (matchedKey !== null) {
            return matchedKey;
        }

        if (dynamicConfig.has('*')) {
            return '*';
        }

        return null;
    },

    shouldSkipDataHandler(this: LoaderNormal, iDataType: string): boolean {
        if (!RegistryStore.containsData(iDataType)) {
            return true;
        }

        if (!this._skipDynamicData || this._skipDynamicData.trim() === '') {
            return false;
        }

        const normalizedPatterns = this._skipDynamicData.trim();
        if (normalizedPatterns === '*') {
            return true;
        }

        const lowerDataType = iDataType.toLowerCase();
        for (const pattern of normalizedPatterns.split(',')) {
            const trimmedPattern = pattern.trim().toLowerCase();
            if (!trimmedPattern) {
                continue;
            }

            if (trimmedPattern === '*' || trimmedPattern === lowerDataType) {
                return true;
            }
        }

        return false;
    },

    tryResolveDynamicDataProvider(this: LoaderNormal, iDataType: string, dynamicConfigAppSite: string, paramFile: string): { dataProvider: ReturnType<typeof RegistryStore.getDynamicData> | null; paramJson: JsonObject } {
        const paramJson = this.getDynamicDataParams(dynamicConfigAppSite, paramFile) ?? new JsonObject();
        if (!RegistryStore.containsData(iDataType)) {
            return { dataProvider: null, paramJson };
        }

        return {
            dataProvider: RegistryStore.getDynamicData(iDataType),
            paramJson
        };
    },

    executeDynamicDataHandler(this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, dataHandler: NonNullable<ReturnType<typeof RegistryStore.getDynamicData>>, getParentJsonValue: GetParentJsonValueHandler | null): JsonObject {
        const handlerStart = performance.now();
        const resultJson = this.executeWithReadOnly(paramJson, componentJson, configJson,
            () => RegistryStore.invokeDynamicData(dataHandler.typeName, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, componentJson, this.getTransientStore(), configJson, getParentJsonValue) ?? new JsonObject());
        this._dynamicDataNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
        return resultJson;
    },

    mergeDynamicDataResult(this: LoaderNormal, componentJson: JsonObject, resultJson: JsonObject | null): void {
        if (resultJson === null) {
            return;
        }

        // Redaction runs BEFORE the mode branch, not inside the merge arm. Replace mode substitutes the
        // handler's whole return for the component data, so redacting only on the merge path would make
        // ReplaceHandlerReturn a way to publish the sensitive values the merge path masks.
        const safeResultJson = JsonUtility.redactSensitive(resultJson);
        if (this._config!.replaceHandlerReturn) {
            this.replaceJsonObjectContents(componentJson, safeResultJson);
            return;
        }

        for (const [key, value] of safeResultJson) {
            componentJson.set(key, value);
        }
    },

    replaceJsonObjectContents(this: LoaderNormal, targetJson: JsonObject, sourceJson: JsonObject): void {
        targetJson.clear();
        for (const [key, value] of sourceJson) {
            targetJson.set(key, value);
        }
    },

    recordDynamicDataResult(this: LoaderNormal, componentName: string, iDataType: string, componentJson: JsonObject): void {
        // componentJson is read-only during the IData handler call. The loader merges
        // the handler's resultJson into componentJson between iterations, so multiple
        // entries for one component alias the latest merged state ("alias-to-final").
        // Tests pass against this aliasing because consumers read the final aggregate state.
        this._dynamicResults.dynamicDataResults.set(`${componentName}_${iDataType}`, JsonValue.object(componentJson));
        this.syncDynamicActionResultsAfterDataMutation(componentName, componentJson);
    },

    syncDynamicActionResultsAfterDataMutation(this: LoaderNormal, componentName: string, afterJson: JsonObject | null): void {
        if (!this._actionMode || afterJson === null) {
            return;
        }

        const prefix = `${componentName}_`.toLowerCase();
        for (const key of Array.from(this._dynamicResults.dynamicActionResults.keys())) {
            if (key.toLowerCase().startsWith(prefix)) {
                this._dynamicResults.dynamicActionResults.set(key, JsonValue.object(JsonUtility.redactSensitive(afterJson)));
            }
        }
    },

    resolveDynamicActionConfigSource(this: LoaderNormal, appSite: string): { dynamicConfig: DynamicConfigMap | null; dynamicConfigAppSite: string } {
        let dynamicConfig = this.getDynamicActionConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (dynamicConfig) {
            return { dynamicConfig, dynamicConfigAppSite };
        }

        for (const searchAppSite of this.buildAppSiteSearchOrder(appSite).slice(1)) {
            dynamicConfig = this.getDynamicActionConfig(searchAppSite);
            if (dynamicConfig) {
                dynamicConfigAppSite = searchAppSite;
                break;
            }
        }

        return { dynamicConfig, dynamicConfigAppSite };
    },

    getDynamicActionMatchKey(this: LoaderNormal, componentName: string, dynamicConfig: DynamicConfigMap): string | null {
        const matchedKey = findKeyInMapCaseInsensitive(dynamicConfig, componentName);
        if (matchedKey !== null) {
            return matchedKey;
        }

        if (dynamicConfig.has('*')) {
            return '*';
        }

        return null;
    },

    shouldSkipActionHandler(this: LoaderNormal, iActionType: string): boolean {
        return false;
    },

    tryResolveDynamicActionProvider(this: LoaderNormal, iActionType: string, dynamicConfigAppSite: string, paramFile: string): { actionProvider: ReturnType<typeof RegistryStore.getDynamicAction> | null; paramJson: JsonObject } {
        const paramJson = this.getDynamicActionParams(dynamicConfigAppSite, paramFile) ?? new JsonObject();
        if (!RegistryStore.containsAction(iActionType)) {
            return { actionProvider: null, paramJson };
        }

        return {
            actionProvider: RegistryStore.getDynamicAction(iActionType),
            paramJson
        };
    },

    tryBuildParentJsonValueHandlerForAction(this: LoaderNormal, appSite: string, flowStack: string[] | null, contextJson: JsonObject | null = null): GetParentJsonValueHandler | null {
        const parentContext = flowStack && flowStack.length > 0 ? this._buildParentContext(appSite, flowStack) : null;
        if (!parentContext && !contextJson) {
            return null;
        }

        return (key: string): string | null => {
            const parentValue = parentContext?.get(key.toLowerCase());
            if (parentValue !== undefined && parentValue !== '') {
                return parentValue;
            }
            if (contextJson?.has(key)) {
                // F-SEC1: surface non-string scalars (notably the IsAuthenticated bool) as their string form.
                // asString() returns null for a bool/number, which previously hid IsAuthenticated from the
                // in-handler authz check and forced it to authorize on UserRole alone.
                const ctxRaw = contextJson.get(key);
                let ctxValue = ctxRaw.asString();
                if ((ctxValue === null || ctxValue === '') && !ctxRaw.isNull) {
                    ctxValue = ctxRaw.toString();
                }
                if (ctxValue !== null && ctxValue !== '') {
                    return ctxValue;
                }
            }
            return parentValue ?? null;
        };
    },

    executeDynamicAction(this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, actionJson: JsonObject | null, actionHandler: NonNullable<ReturnType<typeof RegistryStore.getDynamicAction>>, getParentJsonValue: GetParentJsonValueHandler | null): void {
        this.executeWithReadOnly(
            paramJson,
            componentJson,
            actionJson,
            () => RegistryStore.invokeDynamicAction(actionHandler.typeName, appSite, appView, appFile, componentName, resolvedAppSite, resolvedComponentName, paramJson, componentJson, this.getTransientStore(), actionJson, getParentJsonValue)
        );
    },

    executeDynamicActionHandler(this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, actionJson: JsonObject | null, actionHandler: NonNullable<ReturnType<typeof RegistryStore.getDynamicAction>>, getParentJsonValue: GetParentJsonValueHandler | null): void {
        const handlerStart = performance.now();
        this.executeDynamicAction(appSite, appView, appFile, componentName, resolvedAppSite, resolvedComponentName, paramJson, componentJson, actionJson, actionHandler, getParentJsonValue);
        this._dynamicActionNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
    },

    recordDynamicActionResult(this: LoaderNormal, componentName: string, iActionType: string, componentJson: JsonObject): void {
        // componentJson is read-only during DoAction (executeWithReadOnly) and current
        // IAction handlers communicate via transientStore, never mutating componentJson.
        // syncDynamicActionResultsAfterDataMutation overwrites this entry after any
        // later data merge, so storing the reference here is safe.
        this._dynamicResults.dynamicActionResults.set(`${componentName}_${iActionType}`, JsonValue.object(JsonUtility.redactSensitive(componentJson)));
    },

    buildAppSiteSearchOrder(this: LoaderNormal, appSite: string): string[] {
        const appSitesToSearch: string[] = [appSite];
        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites) {
            for (const s of searchAppSites.split(',')) {
                const trimmed = s.trim();
                if (trimmed && !appSitesToSearch.includes(trimmed)) {
                    appSitesToSearch.push(trimmed);
                }
            }
        }

        return appSitesToSearch;
    },

    // #endregion

    // #region DynamicView Apply (Private)

    /**
     * Applies dynamic view handlers from Global/dynamicview.json to wrap/modify component HTML
     * Handlers are registered IView implementations that wrap HTML at runtime
     * First applies wildcard "*" handlers, then applies specific component handlers
     */
    applyDynamicViewHandlers(this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtml: string, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson: JsonObject | null = null): string {
        if (this.shouldSkipDynamicViewHandlerInvocation(componentHtml, componentName, resolvedComponentName)) return componentHtml;
        return this._applyDynamicViewHandlers(appSite, appView, appFile, componentName, componentDepth, componentHtml, componentJson, resolvedAppSite, resolvedComponentName, configJson);
    },

    _applyDynamicViewHandlers(this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtml: string, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson: JsonObject | null = null): string {
        let { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicViewConfigSource(appSite);
        if (!dynamicConfig) {
            return componentHtml;
        }

        const dynamicViewKeys = this.getDynamicViewKeysToProcess(appSite, componentName, dynamicConfig, dynamicConfigAppSite);
        const keysToProcess = dynamicViewKeys.keysToProcess;
        dynamicConfig = dynamicViewKeys.dynamicConfig;
        dynamicConfigAppSite = dynamicViewKeys.dynamicConfigAppSite;

        if (keysToProcess.length === 0) {
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`ApplyDynamicViewHandlers: No handlers for componentName='${componentName}', available keys: ${[...dynamicConfig.keys()].join(', ')}`, 'LoaderNormal');
            }
            return componentHtml;
        }

        let resultHtml = componentHtml;

        // Process handlers from each matched key
        for (const currentKey of keysToProcess) {
            const handlers: DynamicConfig[] | null = dynamicConfig.get(currentKey);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`ApplyDynamicViewHandlers: Found ${handlers.length} handlers for ${componentName} (key: ${currentKey})`, 'LoaderNormal');
            }

            // Process each handler (handler is typed DynamicConfig)
            for (const handler of handlers) {
                // Get iType (e.g., "BaseView") - typed DynamicConfig property
                const iViewType = handler.iType;
                if (!iViewType) {
                    continue;
                }

                // Check if this handler should be skipped
                if (this.shouldSkipViewHandler(iViewType)) {
                    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                        Logger.debug(`ApplyDynamicViewHandlers: Skipping handler '${iViewType}' due to SkipDynamicView='${this._skipDynamicView}'`, 'LoaderNormal');
                    }
                    continue;
                }

                const paramFile = handler.iParamFile || '';
                const { viewProvider, paramJson } = this.tryResolveDynamicViewProvider(iViewType, dynamicConfigAppSite, paramFile);
                if (!viewProvider) {
                    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                        Logger.debug(`ApplyDynamicViewHandlers: IView handler '${iViewType}' not registered`, 'LoaderNormal');
                    }
                    continue;
                }

                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicViewHandlers: Calling ${iViewType} for ${componentName}`, 'LoaderNormal');
                }

                // Call the handler - returns modified HTML
                // Convert plain object to JsonObject if needed (matches C# LoaderJsonHelper.DictionaryToJsonObject)
                let compJson: JsonObject;
                const compJsonValue: unknown = componentJson;
                if (compJsonValue instanceof JsonObject) {
                    compJson = compJsonValue;
                } else if (compJsonValue instanceof Map) {
                    compJson = new JsonObject();
                    for (const [key, value] of compJsonValue) {
                        compJson.set(key, value);
                    }
                } else if (compJsonValue && typeof compJsonValue === 'object' && !(compJsonValue instanceof JsonArray)) {
                    compJson = JsonObject.fromPlainObject(compJsonValue as PlainJsonObject);
                } else {
                    compJson = new JsonObject();
                }

                try {
                    resultHtml = this.executeDynamicViewHandler(appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, resultHtml, paramJson, compJson, configJson, viewProvider);
                    this._dynamicViewCount++;

                    if (resultHtml.indexOf('\r') !== -1) {
                        resultHtml = CommonUtil.normalizeFileContent(resultHtml);
                    }

                    this.recordDynamicViewResult(componentName, iViewType, resultHtml);
                } catch (ex) {
                    Logger.error(`ApplyDynamicViewHandlers: Error calling ${iViewType}: ${(ex as Error).message}`, 'LoaderNormal');
                    Logger.fallback("F-ENG3", `IView handler threw, component left un-enriched: handler=${iViewType} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                    throw ex;
                }
            }
        }

        return resultHtml;
    },

    // #endregion

    // #region DynamicData Apply (Private)

    /**
     * Applies dynamic data handlers to component JSON (async version)
     */
    async applyDynamicDataHandlers(this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject): Promise<void> {
        let { dynamicConfig: dynamicDataConfig, dynamicConfigAppSite } = this.resolveDynamicDataConfigSource(appSite);
        if (!dynamicDataConfig) {
            return;
        }

        const matchedKey = this.getDynamicDataMatchKey(componentName, dynamicDataConfig);
        if (!matchedKey) {
            return;
        }

        const handlers: DynamicConfig[] | null = dynamicDataConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        for (const handler of handlers) {
            // Get iType (typed DynamicConfig property)
            const handlerName = handler.iType;
            if (!handlerName) {
                continue;
            }

            const { dataProvider: dataHandler, paramJson } = this.tryResolveDynamicDataProvider(handlerName, dynamicConfigAppSite, handler.iParamFile || '');
            if (!dataHandler) {
                continue;
            }

            const resultJson = this.executeDynamicDataHandler(
                appSite,
                appView,
                appFile,
                componentName,
                componentDepth,
                resolvedAppSite,
                resolvedComponentName,
                paramJson,
                componentJson,
                null,
                dataHandler,
                null
            );

            this.mergeDynamicDataResult(componentJson, resultJson);
        }
        JsonUtility.redactSensitiveInPlace(componentJson);
    },

    /**
     * Synchronous version of applyDynamicDataHandlers for use within getTemplateJsonWithInheritance
     * Uses IDataType and IDataParamFile fields from dynamicdata.json
     * @private
     */
    _applyDynamicDataHandlersSync(this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, configJson: JsonObject | null = null, flowStack: string[] | null = null): void {
        let { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicDataConfigSource(appSite);
        if (!dynamicConfig) {
            return;
        }

        // NOTE: Do NOT merge contextJson or configJson into componentJson here.
        // Context merging happens in the engine via JsonMergeUtil (matches C# pattern).
        // configJson is passed as a parameter to handlers, which read from it directly.

        // Check if this component has dynamic data handlers configured (case-insensitive) (dynamicConfig is Map<string, DynamicConfig[]>)
        let matchedKey = this.getDynamicDataMatchKey(componentName, dynamicConfig);
        if (!matchedKey) {
            return;
        }

        const handlers: DynamicConfig[] | null = dynamicConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`ApplyDynamicDataHandlers: Found ${handlers.length} handlers for ${componentName}`, 'LoaderNormal');
        }

        // Process each handler (handler is typed DynamicConfig)
        for (const handler of handlers) {
            // Get iType (e.g., "GetTime") - typed DynamicConfig property
            const iDataType = handler.iType;
            if (!iDataType) {
                continue;
            }

            // Check if this handler should be skipped
            if (this.shouldSkipDataHandler(iDataType)) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicDataHandlers: Skipping handler '${iDataType}' due to SkipDynamicData='${this._skipDynamicData}'`, 'LoaderNormal');
                }
                continue;
            }

            // Get iParamFile (e.g., "DynamicData.GetTime") - typed DynamicConfig property
            const paramFile = handler.iParamFile || '';

            const { dataProvider: dataHandler, paramJson } = this.tryResolveDynamicDataProvider(iDataType, dynamicConfigAppSite, paramFile);
            if (!dataHandler) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicDataHandlers: IData handler '${iDataType}' not registered`, 'LoaderNormal');
                }
                continue;
            }

            // Call the handler synchronously (handlers modify componentJson in-place)
            // Even though getData is async, the synchronous portion runs immediately
            try {
                // Data handlers resolve parent values the SAME way action handlers do: a non-empty inherited
                // parent value wins, otherwise fall back to the server-verified request context. This surfaces
                // auth keys (UserId/IsAuthenticated/UserRole) to context-aware data providers (e.g. listing the
                // signed-in user's own API keys) — the parent-only resolver could not reach the request context.
                const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);
                const resultJson = this.executeDynamicDataHandler(appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, componentJson, configJson, dataHandler, getParentJsonValue);
                this._dynamicDataCount++;
                this.mergeDynamicDataResult(componentJson, resultJson);
                this.recordDynamicDataResult(componentName, iDataType, componentJson);
            } catch (ex) {
                Logger.error(`ApplyDynamicDataHandlers: Error calling ${iDataType}: ${(ex as Error).message}`, 'LoaderNormal');
                Logger.fallback("F-ENG3", `IData handler threw, component left un-enriched: handler=${iDataType} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                throw ex;
            }
        }
    },

    // #endregion

    // #region DynamicClientData Apply (Private)

    resolveDynamicClientDataConfigSource(this: LoaderNormal, appSite: string): { dynamicConfig: DynamicConfigMap | null; dynamicConfigAppSite: string } {
        let dynamicConfig = this.getDynamicClientDataConfig(appSite);
        let dynamicConfigAppSite = appSite;
        if (dynamicConfig) {
            return { dynamicConfig, dynamicConfigAppSite };
        }

        for (const searchAppSite of this.buildAppSiteSearchOrder(appSite).slice(1)) {
            dynamicConfig = this.getDynamicClientDataConfig(searchAppSite);
            if (dynamicConfig) {
                dynamicConfigAppSite = searchAppSite;
                break;
            }
        }

        return { dynamicConfig, dynamicConfigAppSite };
    },

    /**
     * Resolves the params for a PURE client handler — one the AppSite declares with DataSource ⊇ "Pure" in
     * dynamicclientdata.json, meaning it produces its output from its declared params alone and never
     * consumes a server-produced raw island.
     *
     * A pure binding names the IClient processor in its IType (e.g. TimeClient), whereas an Embedded binding
     * names the IClientData preparer. That is what lets one config file carry both shapes without ambiguity,
     * and it is why this matches on the handler name off the element rather than on the island key.
     *
     * Returns null when the AppSite declares no pure binding for this component/handler, which is the signal
     * to skip the handler exactly as an absent island always has.
     */
    tryResolvePureClientHandlerParams(this: LoaderNormal, appSite: string, componentName: string, clientHandlerName: string): JsonObject | null {
        const { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicClientDataConfigSource(appSite);
        if (dynamicConfig === null) {
            return null;
        }

        const matchedKey = this.getDynamicDataMatchKey(componentName, dynamicConfig);
        if (matchedKey === null) {
            return null;
        }

        for (const handler of dynamicConfig.get(matchedKey) ?? []) {
            if (!handler.hasDataSource('Pure')) continue;
            if (handler.iType.toLowerCase() !== clientHandlerName.toLowerCase()) continue;

            return this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
        }

        return null;
    },

    /**
     * Applies CLIENT-DATA preparer handlers from dynamicclientdata.json for a component. Runs the registered
     * IClientData producer (context-light, via RegistryStore.invokeDynamicClientData) and records the RAW set
     * into dynamicClientDataResults — it never mutates componentJson (the raw is for the browser to filter,
     * not the server-baked DataJson). The merge records ONLY when Embedded is declared (page-1 raw ships with
     * the render); a Remote-only binding is served on demand by getClientDataSlice (the /api/handler-data
     * endpoint, P3A), never produced at render. configJson is passed as the producer's callerParams (carries
     * paging keys for a partial/paged slice).
     */
    applyDynamicClientDataHandlers(this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, configJson: JsonObject | null, flowStack: string[] | null = null): void {
        const { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicClientDataConfigSource(appSite);
        if (!dynamicConfig) {
            return;
        }

        const matchedKey = this.getDynamicDataMatchKey(componentName, dynamicConfig);
        if (!matchedKey) {
            return;
        }

        const handlers: DynamicConfig[] | null = dynamicConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

        for (const handler of handlers) {
            const iClientDataType = handler.iType;
            if (!iClientDataType) {
                continue;
            }

            // The merge produces the raw only for an Embedded (or Embedded,Remote hybrid) binding — page-1
            // raw ships with the render. A Remote-only binding is served on demand by getClientDataSlice
            // (the /api/handler-data endpoint), never produced at render, so skip it here.
            if (!handler.hasDataSource('Embedded')) {
                continue;
            }

            if (!RegistryStore.containsClientData(iClientDataType)) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicClientDataHandlers: IClientData handler '${iClientDataType}' not registered`, 'LoaderNormal');
                }
                continue;
            }

            const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();

            try {
                // F2: pin paramJson + componentJson + configJson read-only across the producer — ClientData
                // records the RAW set only and must never mutate the resolved component JSON (invariant I4).
                const handlerStart = performance.now();
                const rawJson = this.executeWithReadOnly(paramJson, componentJson, configJson,
                    () => RegistryStore.invokeDynamicClientData(iClientDataType, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, componentJson, this.getTransientStore(), configJson, getParentJsonValue) ?? new JsonObject());
                this._dynamicClientDataNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                this._dynamicClientDataCount++;
                this._dynamicResults.dynamicClientDataResults.set(`${componentName}_${iClientDataType}`, JsonValue.object(rawJson));
            } catch (ex) {
                Logger.error(`ApplyDynamicClientDataHandlers: Error calling ${iClientDataType}: ${(ex as Error).message}`, 'LoaderNormal');
                // F-ENG3 (handler exception): an IClientData producer threw, so we log it and surface the exception —
                // the component ships without its client-data island and the failure is invisible to the response.
                Logger.fallback('F-ENG3', `IClientData handler threw, island not produced: handler=${iClientDataType} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                throw ex;
            }
        }
    },

    /**
     * CLIENT tier consumption (§2B.5): fills resolvedJson from the delivered island via the registered IClient
     * handler (substitutes for the server data handler that ConfigClient does not ship) and re-emits the island
     * at the page tail — so the client re-merge matches the server render byte-for-byte. Extracts EVERY
     * data-client-handler declaration from the rendered componentHtml; per declaration reads the raw from the
     * delivered map (_config.dynamicClientData) else the HTML island keyed <ComponentName>_<ClientDataKey>, runs
     * the registered IClient over resolvedJson (F2 read-only pin on paramJson+rawJson), merges the filtered
     * result into resolvedJson, records the RAW into dynamicClientDataResults (re-emit) and the FILTERED into
     * dynamicClientResults. Never mutates the server DataJson on the server tier (runs only under clientMode).
     */
    applyClientHandlers(this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentHtml: string | null, resolvedJson: JsonObject, configJson: JsonObject | null, flowStack: string[] | null = null): void {
        if (!componentHtml) {
            return;
        }

        const declarations = CommonUtil.extractClientHandlerDeclarations(componentHtml);
        if (declarations.length === 0) {
            return;
        }

        // Island raw source: the ConfigClient console round-trip carries the delivered map
        // (_config.dynamicClientData); a real browser has no such map and reads the island from the rendered DOM.
        // Prefer the delivered map when it carries the island (keeps the console output byte-identical to the
        // server, whose island is placed at the page tail), else fall back to the HTML island.
        const delivered = this._config!.dynamicClientData;

        // Resolve cross-component parent values (e.g. a split appsite's SelectedAppSiteFolder set by the parent
        // AppSiteFoldersClient) via the SAME delegate the server data handler uses, so the client consumer
        // resolves configJson > componentJson > parent identically to ScenariosData.processData.
        const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, this._requestContextJson);

        for (const decl of declarations) {
            if (!RegistryStore.containsClientHandler(decl.handlerName)) {
                continue;
            }

            let rawJson: JsonObject;
            let pureParamJson: JsonObject | null = null;
            // The island this declaration CONSUMES. Empty on the pure lane, which consumes none.
            let islandKey = '';
            // The key the consumed OUTPUT is recorded under. Always set, so a pure declaration is still
            // cross-checked by the round-trip: named for the preparer on the island lane (symmetric with
            // dynamicClientDataResults) and for the handler itself on the pure lane, which has no preparer.
            let resultKey = componentName + '_' + decl.handlerName;
            if (!decl.clientDataKey) {
                // PURE lane, DECLARED by the absence of data-client-data: this handler derives its output from
                // its params alone, so no island is looked up at all. The AppSite's dynamicclientdata binding
                // must still declare DataSource "Pure" for it to run, which keeps the JSON declaration the
                // security bound — a component cannot opt itself into island-free execution by editing its HTML.
                pureParamJson = this.tryResolvePureClientHandlerParams(appSite, componentName, decl.handlerName);
                if (pureParamJson === null) {
                    continue;
                }
                rawJson = new JsonObject();
            } else {
                islandKey = componentName + '_' + decl.clientDataKey;
                resultKey = islandKey;
                if (delivered !== null && delivered.has(islandKey) && delivered.get(islandKey).isObject) {
                    rawJson = delivered.get(islandKey).asObject()!;
                } else {
                    const rawStr = CommonUtil.extractClientDataIsland(componentHtml, islandKey);
                    // A DATA-DEPENDENT declaration named a preparer, so a missing island is a MISSING PRODUCER
                    // and the handler is skipped. ⛔ It is deliberately NOT retried on the pure path: the two
                    // lanes are disjoint by declaration now, so a broken producer can no longer be masked by a
                    // pure binding that happens to share the handler name.
                    if (!rawStr) {
                        continue;
                    }
                    rawJson = JsonUtility.parseJsonString(rawStr);
                    if (rawJson.size === 0) {
                        continue;
                    }
                }
            }

            try {
                // A pure handler reads its params from the BINDING (where FixedTime/DateFormat live alongside
                // the server tier's own copy), overlaid with the element's data-client-handler-param so a
                // per-instance override still wins. A data-dependent handler keeps reading the element only.
                let paramJson: JsonObject;
                if (pureParamJson === null) {
                    paramJson = JsonUtility.parseJsonString(decl.param) ?? new JsonObject();
                } else {
                    paramJson = pureParamJson;
                    const declOverride = JsonUtility.parseJsonString(decl.param);
                    if (declOverride !== null) {
                        for (const [k, v] of declOverride.entries()) {
                            paramJson.set(k, v);
                        }
                    }
                }
                // F2: pin the delivered island raw + paramJson read-only across the IClient invocation — a
                // misbehaving filter must not mutate the delivered island (re-emitted at the page tail) or its
                // params. resolvedJson is the intended mutation target (left writable); configJson/callerParams
                // is a freshly-built per-request object (safe), so it is not pinned here.
                const handlerStart = performance.now();
                const filtered = this.executeWithReadOnly(paramJson, rawJson, null,
                    () => RegistryStore.invokeDynamicClient(decl.handlerName, appSite, appView, appFile, componentName, componentDepth, resolvedAppSite, resolvedComponentName, paramJson, resolvedJson, this.getTransientStore(), configJson, getParentJsonValue, rawJson) ?? new JsonObject());
                this._dynamicClientDataNanos += Math.round((performance.now() - handlerStart) * 1_000_000);
                this._dynamicClientDataCount++;
                for (const [k, v] of filtered.entries()) {
                    resolvedJson.set(k, v);
                }
                // Re-emit the delivered island at the page tail (the top-level merge appends
                // dynamicClientDataResults), matching the server byte-for-byte. A PURE handler consumed no
                // island — rawJson is the empty stand-in built above, not a delivered payload — so there is
                // nothing to re-emit. Emitting it would append an island the server never produced and make
                // every pure row fail the client-vs-server compare on that one trailing script tag.
                if (pureParamJson === null) {
                    this._dynamicResults.dynamicClientDataResults.set(islandKey, JsonValue.object(rawJson));
                }
                // Record the CONSUMED (filtered) output — the client mirror of the server DynamicData — so the
                // round-trip can cross-check every engine's consumer, symmetric to dynamicClientDataResults.
                // Keyed by resultKey, NOT islandKey: a pure declaration has no island key, and recording it
                // under an empty key would collide every pure handler in the component onto one slot.
                this._dynamicResults.dynamicClientResults.set(resultKey, JsonValue.object(filtered));
            } catch (ex) {
                Logger.error(`ApplyClientHandlers: Error running ${decl.handlerName}: ${(ex as Error).message}`, 'LoaderNormal');
                // F-ENG3 (handler exception): an IClient consumer threw, so we log it and surface the exception — the
                // component keeps its un-enriched JSON and the failure is invisible to the response.
                Logger.fallback('F-ENG3', `IClient handler threw, component left un-enriched: handler=${decl.handlerName} component=${componentName} appSite=${appSite} appView=${appView} ex=${(ex as Error).message}`);
                throw ex;
            }
        }
    },

    /**
     * P3A ON-DEMAND slice for the /api/handler-data endpoint. Resolves the client-data binding for handlerName
     * and returns the raw producer output via the SAME context-light producer the merge uses
     * (RegistryStore.invokeDynamicClientData) — one producer, two seams. Does NOT run a page merge; callerParams
     * is handed straight in as paging keys. Returns null when the AppSite declares no binding for the handler
     * (endpoint → 400, no arbitrary handler probing).
     *
     * Not gated on DataSource ⊇ Remote: that flag says WHERE the island is delivered at render time (Embedded =
     * with the server's own render), not who is allowed to ask for it. A client rendering the AppSite itself
     * needs exactly the data the Embedded island would have carried, so gating here would force every Embedded
     * AppSite to redeclare itself merely to be client-mergeable. The declaration in dynamicclientdata.json
     * remains the bound: an undeclared handler is still unreachable.
     */
    getClientDataSlice(this: LoaderNormal, appSite: string, handlerName: string, callerParams: JsonObject): JsonObject | null {
        if (!handlerName) {
            return null;
        }

        const { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicClientDataConfigSource(appSite);
        if (!dynamicConfig) {
            return null;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers: DynamicConfig[] | null = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (!handler.iType || handler.iType.toLowerCase() !== handlerName.toLowerCase()) {
                    continue;
                }
                if (!RegistryStore.containsClientData(handlerName)) {
                    return null;
                }

                const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
                // Remote/on-demand endpoint: no merge context, so the merge-only params are passed empty and the
                // per-request config rides in as configJson (the producer reads only appSite/component/paramJson).
                return RegistryStore.invokeDynamicClientData(handlerName, appSite, '', '', boundComponentName, 0, appSite, boundComponentName, paramJson, new JsonObject(), this.getTransientStore(), callerParams, null) ?? new JsonObject();
            }
        }

        return null;
    },

    /**
     * Produces every declared client-data island for the AppSite, keyed "<component>_<IType>" — the island set a
     * client merge of this AppSite needs when the page carries none of its own (see ILoader.getClientDataIslands).
     * Runs the same context-light producer as the merge and the single-slice endpoint, over every binding
     * dynamicclientdata.json declares, so the set is byte-identical to the islands a server render of this
     * AppSite would have embedded.
     */
    getClientDataIslands(this: LoaderNormal, appSite: string, callerParams: JsonObject): JsonObject {
        const islands = new JsonObject();

        const { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicClientDataConfigSource(appSite);
        if (!dynamicConfig) {
            return islands;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers: DynamicConfig[] | null = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (!handler.iType || !RegistryStore.containsClientData(handler.iType)) {
                    continue;
                }

                const paramJson = this.getDynamicClientDataParams(dynamicConfigAppSite, handler.iParamFile) ?? new JsonObject();
                // Same shape as the merge's applyDynamicClientDataHandlers record: key by the BOUND component,
                // because that is the component the consumer's island lookup derives (component + "_" + key).
                const raw = RegistryStore.invokeDynamicClientData(handler.iType, appSite, '', '', boundComponentName, 0, appSite, boundComponentName, paramJson, new JsonObject(), this.getTransientStore(), callerParams, null);
                if (raw) {
                    islands.set(boundComponentName + '_' + handler.iType, JsonValue.object(raw));
                }
            }
        }

        return islands;
    },

    /**
     * True when the AppSite declares a client-data binding with a registered producer (see ILoader.hasClientData).
     * Same predicate as getClientDataIslands, declaration lookup only.
     */
    hasClientData(this: LoaderNormal, appSite: string): boolean {
        const { dynamicConfig } = this.resolveDynamicClientDataConfigSource(appSite);
        if (!dynamicConfig) {
            return false;
        }

        for (const boundComponentName of dynamicConfig.keys()) {
            const handlers: DynamicConfig[] | null = dynamicConfig.get(boundComponentName);
            if (!handlers || !Array.isArray(handlers)) {
                continue;
            }
            for (const handler of handlers) {
                if (handler.iType && RegistryStore.containsClientData(handler.iType)) {
                    return true;
                }
            }
        }

        return false;
    },

    // #endregion

    // #region DynamicAction Apply (Private)

    /**
     * Synchronous version of applyDynamicActionHandlers for use within getTemplateJsonWithInheritance
     * Uses IActionType and IActionParamFile fields from dynamicaction.json
     * Only executes when actionMode is true
     * @private
     */
    applyDynamicActionHandlers(this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentJson: JsonObject, resolvedAppSite: string, resolvedComponentName: string, configJson: JsonObject | null = null, contextJson: JsonObject | null = null, flowStack: string[] | null = null): void {
        // Only execute if actionMode is true
        if (!this._actionMode) {
            return;
        }

        let { dynamicConfig, dynamicConfigAppSite } = this.resolveDynamicActionConfigSource(appSite);
        if (!dynamicConfig) {
            return;
        }

        let matchedKey = this.getDynamicActionMatchKey(componentName, dynamicConfig);
        if (!matchedKey) {
            return;
        }

        const handlers: DynamicConfig[] | null = dynamicConfig.get(matchedKey);
        if (!handlers || !Array.isArray(handlers)) {
            return;
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`ApplyDynamicActionHandlers: Found ${handlers.length} handlers for ${componentName}`, 'LoaderNormal');
        }

        // configJson contains action data (e.g., {"ActionMode": true, "Action": "Increment"})
        // Pass it directly as actionJson to the handler (matches C# behavior)
        const actionJson = configJson;

        // Process each handler
        for (const handlerObj of handlers) {
            // Handler is a typed DynamicConfig object
            // Get iType (e.g., "CounterAction")
            const iActionType = handlerObj.iType;
            if (!iActionType) {
                continue;
            }

            if (this.shouldSkipActionHandler(iActionType)) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicActionHandlers: Skipping handler '${iActionType}'`, 'LoaderNormal');
                }
                continue;
            }

            const paramFile = handlerObj.iParamFile || '';

            const { actionProvider: actionHandler, paramJson } = this.tryResolveDynamicActionProvider(iActionType, dynamicConfigAppSite, paramFile);
            if (!actionHandler) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicActionHandlers: IAction handler '${iActionType}' not registered`, 'LoaderNormal');
                }
                continue;
            }

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`ApplyDynamicActionHandlers: Calling ${iActionType} for ${componentName}`, 'LoaderNormal');
            }

            // Call the handler synchronously
            try {
                const getParentJsonValue = this.tryBuildParentJsonValueHandlerForAction(appSite, flowStack, contextJson ?? this._requestContextJson);
                this.executeDynamicActionHandler(appSite, appView, appFile, componentName, resolvedAppSite, resolvedComponentName, paramJson, componentJson, actionJson, actionHandler, getParentJsonValue);
                this._dynamicActionCount++;
                this.recordDynamicActionResult(componentName, iActionType, componentJson);
            } catch (ex) {
                Logger.error(`ApplyDynamicActionHandlers: Error calling ${iActionType}: ${(ex as Error).message}`, 'LoaderNormal');
            }
        }
    },

    // #endregion

    // #region DynamicAppSite Apply (Public)

    /**
     * Resolves the AppSite/AppView to render by running the IAppSite handlers configured for the
     * requested AppSite (dynamicappsite.json, keyed by AppSite name). Mirrors applyDynamicDataHandlers
     * ownership: the loader owns config lookup, provider dispatch, and reload of any redefined target.
     * Returns the requested AppSite/AppView unchanged when no handler is configured or registered.
     *
     * AppSite handlers run additively: AppSite-specific handlers first, then the wwwroot-level wildcard
     * ("*") handlers LAST (global rule stays authoritative). This is dormant infrastructure until IAppSite
     * handlers are registered and the merge pipeline calls it (a later phase).
     */
    applyDynamicAppSiteHandlers(this: LoaderNormal, appSite: string, appView: string | null, contextJson: JsonObject, configJson: JsonObject | null): AppSiteResult {
        // Stash the request context for the per-merge handler authorization (always, even when no AppSite
        // handlers are configured). This runs once at the engine merge top.
        this._requestContextJson = contextJson;
        // Reset the per-merge navigation trace; one decision is appended per handler invocation below.
        this._lastNavigationTrace = [];

        let curAppSite = appSite;
        let curAppView = appView ?? '';

        const appSiteConfig = this.getDynamicAppSiteConfig(appSite);
        if (!appSiteConfig) return new AppSiteResult(curAppSite, curAppView);

        const specificHandlers = appSiteConfig.get(appSite);
        const wildcardHandlers = appSite === '*' ? null : appSiteConfig.get('*');
        const handlers: DynamicConfig[] = [];
        if (specificHandlers && Array.isArray(specificHandlers)) handlers.push(...specificHandlers);
        if (wildcardHandlers && Array.isArray(wildcardHandlers)) handlers.push(...wildcardHandlers);
        if (handlers.length === 0) return new AppSiteResult(curAppSite, curAppView);

        for (const handler of handlers) {
            const iAppSiteType = handler.iType;
            if (!iAppSiteType) {
                continue;
            }

            const provider = RegistryStore.getDynamicAppSite(iAppSiteType);
            if (!provider) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`ApplyDynamicAppSiteHandlers: IAppSite handler '${iAppSiteType}' not registered`, 'LoaderNormal');
                }
                continue;
            }

            // Params are stored under the ORIGINAL appSite (where dynamicappsite.json lives).
            const paramJson = this.getDynamicAppSiteParams(appSite, handler.iParamFile || '') ?? new JsonObject();
            try {
                const resolved = RegistryStore.invokeDynamicAppSite(provider.typeName, curAppSite, curAppView, paramJson, contextJson, configJson);
                if (resolved) {
                    const changed = curAppSite !== resolved.appSite || curAppView !== resolved.appView;
                    if (changed) {
                        Logger.flow(`ApplyDynamicAppSiteHandlers: redefined ${curAppSite}/${curAppView} -> ${resolved.appSite}/${resolved.appView} via ${iAppSiteType}`, 'Normal.MergeLifecycle');
                    }
                    // Preserve every hop (change or no-op) so a scenario can assert the path, not only the endpoint.
                    this._lastNavigationTrace.push(new NavigationDecision(
                        iAppSiteType, curAppSite, curAppView, resolved.appSite, resolved.appView, changed, resolved.reason));
                    curAppSite = resolved.appSite;
                    curAppView = resolved.appView;
                }
            } catch (ex) {
                // F-SEC5 (hardened, fail-closed): an AppSite handler computes the authoritative auth redirect
                // (e.g. unauth Admin -> Login). If it throws, keeping curAppSite/curAppView at the requested
                // (protected) values would render the PROTECTED page — a fail-OPEN. Surface the failure instead.
                Logger.error(`ApplyDynamicAppSiteHandlers: Error calling ${iAppSiteType}: ${(ex as Error).message}`, 'LoaderNormal');
                Logger.fallback("F-SEC5", `AppSite handler ${iAppSiteType} threw; failing closed (would have kept protected appSite=${curAppSite}/${curAppView}): ${(ex as Error).message}`);
                throw ex;
            }
        }

        // The loader was loaded for the original AppSite; if a handler redefined it, ensure the new
        // AppSite's templates/data are loaded so appfile/template resolution finds them.
        if (curAppSite !== appSite) {
            this.ensureAppSiteLoaded(curAppSite);
        }

        return new AppSiteResult(curAppSite, curAppView);
    }

    // #endregion
};
